import React, { useMemo } from 'react';
import type { Locale } from '../studioLocale';

export interface InspectorEnums {
  CharacterType: string[];
  ConflictType: string[];
  ActType: string[];
  BeatType: string[];
  GenreType: string[];
  EmotionType: string[];
  WritingStyle: string[];
  WritingTone: string[];
}

interface DeviceTypeOption {
  type: string;
  expected_evidence?: string[];
  short_drama_function?: string;
  short_drama_tags?: string[];
  recommended_catharsis_types?: string[];
  catharsis_tag_pool?: string[];
  recommended_reversal_types?: string[];
  narrative_layer?: string;
  owner_agent?: string;
}

interface ElementInspectorFieldsProps {
  locale: Locale;
  selected: any;
  elements: any[];
  draftData: Record<string, any>;
  enumOptions: InspectorEnums;
  deviceTypes: DeviceTypeOption[];
  onChangeDraft: (nextDraft: Record<string, any>) => void;
}

const COPY = {
  zh: {
    linesHint: '支持换行或逗号分隔。',
    optionsHint: '当前对象：',
    sectionLabels: {
      world_state: '世界观与世界规则',
      theme: '主题属性',
      character: '人物属性',
      arc_milestone: '角色弧线里程碑',
      scene: '场景属性',
      event: '事件属性',
      plot_device: '戏剧装置属性',
      dialogue: '对白属性',
    },
    labels: {
      genre: '类型',
      timePeriod: '时代',
      location: '地点',
      culture: '文化氛围',
      technologyLevel: '技术水平',
      magicSystem: '特殊系统',
      politicalSystem: '权力结构',
      socialHierarchy: '社会阶层',
      rules: '世界规则',
      description: '描述',
      importance: '重要度',
      examples: '例子 / 母题',
      relatedCharacters: '相关人物',
      relatedEvents: '相关事件',
      characterType: '人物类型',
      age: '年龄',
      gender: '性别',
      appearance: '外形',
      background: '背景',
      motivation: '动机',
      goal: '目标',
      obstacle: '阻碍',
      arc: '角色弧线',
      personalityTraits: '性格特征',
      dialogueStyle: '对白风格',
      writingStyle: '写作风格预设',
      writingTone: '语气预设',
      note: '说明',
      character: '人物',
      milestoneType: '里程碑类型',
      attachedEvent: '绑定事件',
      attachedBeat: '绑定节拍',
      act: '幕次',
      episode: '所属集数',
      setting: '场景地点',
      time: '时间',
      mood: '氛围',
      purpose: '场景目的',
      conflict: '冲突核心',
      tensionLevel: '张力等级',
      conflictAxes: '冲突维度',
      conflictIntensity: '冲突强度',
      conflictRoles: '冲突相关角色',
      conflictPairs: '角色冲突对',
      charactersPresent: '出场人物',
      events: '关联事件',
      scriptText: '场景文本',
      eventType: '事件类型',
      scene: '所属场景',
      beat: '节拍',
      charactersInvolved: '涉及人物',
      conflictType: '冲突类型',
      emotionalImpact: '情绪影响',
      consequences: '后果',
      foreshadowing: '伏笔',
      deviceType: '装置类型',
      attachedScene: '绑定场景',
      setupNodes: 'Setup 节点',
      payoffNodes: 'Payoff 节点',
      linkScenes: '关联场景',
      linkCharacters: '关联人物',
      linkRelationships: '关联连线',
      linkBeats: '关联节拍',
      linkWorldRules: '关联世界规则',
      linkProps: '关联道具 / 物件',
      speaker: '说话人',
      content: '对白内容',
      subtext: '潜台词',
      emotion: '情绪',
      dialoguePurpose: '对白目的',
      characterDevelopment: '角色推进',
      plotAdvancement: '情节推进',
      none: '未设置',
      deviceHint: '可输入自定义装置类型，系统会优先建议已知类型。',
      dramaTags: '短剧标签',
      catharsisTypes: '爽点类型',
      catharsisTagPool: '爽点标签池',
      reversalTypes: '反转机制',
      narrativeLayer: '叙事层级',
      ownerAgent: '负责代理',
      setupHint: '每行一种绑定，格式如：scene:场景ID|提示 或 beat:MIDPOINT|提示。',
      conflictPairHint: '每行一组，格式：source|target|intensity|note。',
      styleNote: '这些字段会直接进入结构化草稿，并参与后续生成与局部重写。',
      advancedJson: '高级 JSON',
    },
  },
  en: {
    linesHint: 'Use one item per line or separate with commas.',
    optionsHint: 'Available items:',
    sectionLabels: {
      world_state: 'World settings',
      theme: 'Theme fields',
      character: 'Character fields',
      arc_milestone: 'Arc milestone fields',
      scene: 'Scene fields',
      event: 'Event fields',
      plot_device: 'Plot device fields',
      dialogue: 'Dialogue fields',
    },
    labels: {
      genre: 'Genre',
      timePeriod: 'Time period',
      location: 'Location',
      culture: 'Culture',
      technologyLevel: 'Technology level',
      magicSystem: 'Special system',
      politicalSystem: 'Political system',
      socialHierarchy: 'Social hierarchy',
      rules: 'World rules',
      description: 'Description',
      importance: 'Importance',
      examples: 'Examples / motifs',
      relatedCharacters: 'Related characters',
      relatedEvents: 'Related events',
      characterType: 'Character type',
      age: 'Age',
      gender: 'Gender',
      appearance: 'Appearance',
      background: 'Background',
      motivation: 'Motivation',
      goal: 'Goal',
      obstacle: 'Obstacle',
      arc: 'Arc',
      personalityTraits: 'Personality traits',
      dialogueStyle: 'Dialogue style',
      writingStyle: 'Writing style preset',
      writingTone: 'Tone preset',
      note: 'Note',
      character: 'Character',
      milestoneType: 'Milestone type',
      attachedEvent: 'Attached event',
      attachedBeat: 'Attached beat',
      act: 'Act',
      episode: 'Episode',
      setting: 'Setting',
      time: 'Time',
      mood: 'Mood',
      purpose: 'Purpose',
      conflict: 'Core conflict',
      tensionLevel: 'Tension level',
      conflictAxes: 'Conflict axes',
      conflictIntensity: 'Conflict intensity',
      conflictRoles: 'Conflict roles',
      conflictPairs: 'Conflict pairs',
      charactersPresent: 'Characters present',
      events: 'Linked events',
      scriptText: 'Scene script',
      eventType: 'Event type',
      scene: 'Scene',
      beat: 'Beat',
      charactersInvolved: 'Characters involved',
      conflictType: 'Conflict type',
      emotionalImpact: 'Emotional impact',
      consequences: 'Consequences',
      foreshadowing: 'Foreshadowing',
      deviceType: 'Device type',
      attachedScene: 'Attached scene',
      setupNodes: 'Setup nodes',
      payoffNodes: 'Payoff nodes',
      linkScenes: 'Linked scenes',
      linkCharacters: 'Linked characters',
      linkRelationships: 'Linked relations',
      linkBeats: 'Linked beats',
      linkWorldRules: 'Linked world rules',
      linkProps: 'Linked props',
      speaker: 'Speaker',
      content: 'Content',
      subtext: 'Subtext',
      emotion: 'Emotion',
      dialoguePurpose: 'Dialogue purpose',
      characterDevelopment: 'Character development',
      plotAdvancement: 'Plot advancement',
      none: 'Not set',
      deviceHint: 'Custom device types are allowed. Known types are suggested below.',
      dramaTags: 'Drama tags',
      catharsisTypes: 'Catharsis types',
      catharsisTagPool: 'Catharsis tag pool',
      reversalTypes: 'Reversal types',
      narrativeLayer: 'Narrative layer',
      ownerAgent: 'Owner agent',
      setupHint: 'One binding per line, for example scene:scene_id|hint or beat:MIDPOINT|hint.',
      conflictPairHint: 'One pair per line: source|target|intensity|note.',
      styleNote: 'These fields stay in the structured draft and are used during future generation and partial rewrites.',
      advancedJson: 'Advanced JSON',
    },
  },
} as const;

const cloneDraft = (value: Record<string, any>) => JSON.parse(JSON.stringify(value || {}));

const parseLineList = (value: string) =>
  String(value || '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const formatLineList = (value: any) => (Array.isArray(value) ? value.filter(Boolean).join('\n') : '');

const getValue = (source: Record<string, any>, path: string[], fallback: any = '') =>
  path.reduce<any>((current, key) => (current && typeof current === 'object' ? current[key] : undefined), source) ?? fallback;

const setValue = (source: Record<string, any>, path: string[], value: any) => {
  const next = cloneDraft(source);
  let cursor: Record<string, any> = next;
  path.forEach((key, index) => {
    if (index === path.length - 1) {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete cursor[key];
      } else {
        cursor[key] = value;
      }
      return;
    }
    if (!cursor[key] || typeof cursor[key] !== 'object' || Array.isArray(cursor[key])) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  });
  return next;
};

const parseBindingLines = (value: string) =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawBinding, hint = ''] = line.split('|');
      const [scope, bindingValue] = String(rawBinding || '').split(':');
      const entry: Record<string, any> = {};
      if (scope === 'scene') {
        entry.scene_id = bindingValue || '';
      } else if (scope === 'event') {
        entry.event_id = bindingValue || '';
      } else if (scope === 'beat') {
        entry.beat_id = bindingValue || '';
      } else if (scope === 'act') {
        entry.act_id = bindingValue || '';
      } else if (rawBinding) {
        entry.scene_id = rawBinding.trim();
      }
      if (hint.trim()) {
        entry.hint = hint.trim();
      }
      return entry;
    })
    .filter((item) => Object.keys(item).length > 0);

const formatBindingLines = (value: any) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => {
      const base =
        (entry?.scene_id && `scene:${entry.scene_id}`) ||
        (entry?.event_id && `event:${entry.event_id}`) ||
        (entry?.beat_id && `beat:${entry.beat_id}`) ||
        (entry?.act_id && `act:${entry.act_id}`) ||
        '';
      if (!base) {
        return '';
      }
      return entry?.hint ? `${base}|${entry.hint}` : base;
    })
    .filter(Boolean)
    .join('\n');

const parseConflictPairs = (value: string) =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [source_id = '', target_id = '', intensity = '', note = ''] = line.split('|').map((item) => item.trim());
      const next: Record<string, any> = { source_id, target_id };
      if (intensity) {
        const numeric = Number(intensity);
        if (Number.isFinite(numeric)) {
          next.intensity = numeric;
        }
      }
      if (note) {
        next.note = note;
      }
      return next;
    })
    .filter((item) => item.source_id && item.target_id);

const formatConflictPairs = (value: any) =>
  (Array.isArray(value) ? value : [])
    .map((entry) =>
      [entry?.source_id || '', entry?.target_id || '', entry?.intensity || '', entry?.note || '']
        .filter((item, index) => item || index < 2)
        .join('|')
    )
    .filter(Boolean)
    .join('\n');

const helperSummary = (items: any[], prefix: string) => {
  if (!items.length) {
    return '';
  }
  return `${prefix}${items
    .slice(0, 8)
    .map((item) => item.name || item.data?.title || item.id)
    .join(' / ')}${items.length > 8 ? ' …' : ''}`;
};

const ElementInspectorFields: React.FC<ElementInspectorFieldsProps> = ({
  locale,
  selected,
  elements,
  draftData,
  enumOptions,
  deviceTypes,
  onChangeDraft,
}) => {
  const text = COPY[locale];
  const labels = text.labels;
  const characters = useMemo(() => elements.filter((item) => item.type === 'character'), [elements]);
  const scenes = useMemo(() => elements.filter((item) => item.type === 'scene'), [elements]);
  const events = useMemo(() => elements.filter((item) => item.type === 'event'), [elements]);

  if (!selected) {
    return null;
  }

  const sectionTitle =
    text.sectionLabels[selected.type as keyof typeof text.sectionLabels] || text.sectionLabels.character;

  const apply = (path: string[], value: any) => {
    onChangeDraft(setValue(draftData, path, value));
  };

  const knownDevice = deviceTypes.find((item) => item.type === getValue(draftData, ['device_type'], ''));

  const renderTextInput = (
    label: string,
    path: string[],
    options?: { type?: string; placeholder?: string; full?: boolean }
  ) => (
    <label className={`inspector-block${options?.full ? ' inspector-block--full' : ''}`}>
      <span className="form-label">{label}</span>
      <input
        className="app-input"
        type={options?.type || 'text'}
        value={String(getValue(draftData, path, ''))}
        placeholder={options?.placeholder || ''}
        onChange={(event) => {
          if (options?.type === 'number') {
            const raw = event.target.value.trim();
            if (!raw) {
              apply(path, undefined);
              return;
            }
            const numeric = Number(raw);
            apply(path, Number.isFinite(numeric) ? numeric : undefined);
            return;
          }
          apply(path, event.target.value);
        }}
      />
    </label>
  );

  const renderTextArea = (
    label: string,
    path: string[],
    options?: { rows?: number; full?: boolean; placeholder?: string; helper?: string }
  ) => (
    <label className={`inspector-block${options?.full ? ' inspector-block--full' : ''}`}>
      <span className="form-label">{label}</span>
      <textarea
        className="app-textarea app-textarea--compact"
        rows={options?.rows || 4}
        value={String(getValue(draftData, path, ''))}
        placeholder={options?.placeholder || ''}
        onChange={(event) => apply(path, event.target.value)}
      />
      {options?.helper && <span className="inspector-helper">{options.helper}</span>}
    </label>
  );

  const renderSelect = (
    label: string,
    path: string[],
    options: string[],
    extra?: { full?: boolean; allowEmpty?: boolean }
  ) => (
    <label className={`inspector-block${extra?.full ? ' inspector-block--full' : ''}`}>
      <span className="form-label">{label}</span>
      <select
        className="app-input"
        value={String(getValue(draftData, path, ''))}
        onChange={(event) => apply(path, event.target.value || undefined)}
      >
        {extra?.allowEmpty !== false && <option value="">{labels.none}</option>}
        {options.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </label>
  );

  const renderListArea = (
    label: string,
    path: string[],
    helper?: string,
    full = false
  ) => (
    <label className={`inspector-block${full ? ' inspector-block--full' : ''}`}>
      <span className="form-label">{label}</span>
      <textarea
        className="app-textarea app-textarea--compact"
        rows={4}
        value={formatLineList(getValue(draftData, path, []))}
        placeholder={text.linesHint}
        onChange={(event) => apply(path, parseLineList(event.target.value))}
      />
      {helper && <span className="inspector-helper">{helper}</span>}
    </label>
  );

  const sharedCharacterHint = helperSummary(characters, text.optionsHint);
  const sharedSceneHint = helperSummary(scenes, text.optionsHint);
  const sharedEventHint = helperSummary(events, text.optionsHint);
  const knownDeviceTags = [
    ...(knownDevice?.short_drama_tags || []),
    ...(knownDevice?.catharsis_tag_pool || []),
  ].filter(Boolean);

  return (
    <div className="inspector-fields">
      <div className="inspector-section-title">{sectionTitle}</div>

      {selected.type === 'world_state' && (
        <div className="inspector-grid">
          {renderSelect(labels.genre, ['genre'], enumOptions.GenreType)}
          {renderTextInput(labels.timePeriod, ['time_period'])}
          {renderTextInput(labels.location, ['location'])}
          {renderTextInput(labels.technologyLevel, ['technology_level'])}
          {renderTextInput(labels.culture, ['culture'], { full: true })}
          {renderTextInput(labels.magicSystem, ['magic_system'])}
          {renderTextInput(labels.politicalSystem, ['political_system'])}
          {renderTextInput(labels.socialHierarchy, ['social_hierarchy'])}
          {renderListArea(labels.rules, ['rules'], undefined, true)}
        </div>
      )}

      {selected.type === 'theme' && (
        <div className="inspector-grid">
          {renderTextArea(labels.description, ['description'], { full: true })}
          {renderTextInput(labels.importance, ['importance'], { type: 'number' })}
          {renderListArea(labels.examples, ['examples'], text.linesHint, true)}
          {renderListArea(labels.relatedCharacters, ['related_characters'], sharedCharacterHint, true)}
          {renderListArea(labels.relatedEvents, ['related_events'], sharedEventHint, true)}
        </div>
      )}

      {selected.type === 'character' && (
        <>
          <div className="inspector-style-note">
            <strong>{labels.dialogueStyle}</strong>
            <span>{labels.styleNote}</span>
          </div>
          <div className="inspector-grid">
            {renderSelect(labels.characterType, ['character_type'], enumOptions.CharacterType)}
            {renderTextInput(labels.age, ['age'], { type: 'number' })}
            {renderTextInput(labels.gender, ['gender'])}
            {renderTextInput(labels.appearance, ['appearance'])}
            {renderTextArea(labels.background, ['background'], { full: true })}
            {renderTextArea(labels.motivation, ['motivation'])}
            {renderTextArea(labels.goal, ['goal'])}
            {renderTextArea(labels.obstacle, ['obstacle'])}
            {renderTextArea(labels.arc, ['arc'], { full: true })}
            {renderListArea(labels.personalityTraits, ['personality_traits'], text.linesHint, true)}
            {renderTextArea(labels.dialogueStyle, ['dialogue_style'], { full: true })}
            {renderSelect(labels.writingStyle, ['writing_style'], enumOptions.WritingStyle)}
            {renderSelect(labels.writingTone, ['writing_tone'], enumOptions.WritingTone)}
          </div>
        </>
      )}

      {selected.type === 'arc_milestone' && (
        <div className="inspector-grid">
          <label className="inspector-block">
            <span className="form-label">{labels.character}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['character_id'], ''))}
              onChange={(event) => apply(['character_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {characters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {renderTextInput(labels.milestoneType, ['milestone_type'])}
          {renderSelect(labels.attachedBeat, ['attached_beat'], enumOptions.BeatType)}
          <label className="inspector-block">
            <span className="form-label">{labels.attachedEvent}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['attached_event_id'], ''))}
              onChange={(event) => apply(['attached_event_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {events.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {renderTextArea(labels.note, ['note'], { full: true })}
        </div>
      )}

      {selected.type === 'scene' && (
        <div className="inspector-grid">
          {renderSelect(labels.act, ['act'], enumOptions.ActType)}
          {renderTextInput(labels.episode, ['episode'])}
          {renderTextInput(labels.setting, ['setting'])}
          {renderTextInput(labels.time, ['time'])}
          {renderTextInput(labels.mood, ['mood'])}
          {renderTextArea(labels.purpose, ['purpose'])}
          {renderTextArea(labels.conflict, ['conflict'])}
          {renderTextInput(labels.tensionLevel, ['tension_level'], { type: 'number' })}
          {renderTextInput(labels.conflictIntensity, ['conflict_intensity'], { type: 'number' })}
          {renderListArea(labels.conflictAxes, ['conflict_axes'])}
          {renderListArea(labels.conflictRoles, ['conflict_roles'], sharedCharacterHint)}
          <label className="inspector-block inspector-block--full">
            <span className="form-label">{labels.conflictPairs}</span>
            <textarea
              className="app-textarea app-textarea--compact"
              rows={4}
              value={formatConflictPairs(getValue(draftData, ['conflict_pairs'], []))}
              placeholder={labels.conflictPairHint}
              onChange={(event) => apply(['conflict_pairs'], parseConflictPairs(event.target.value))}
            />
            <span className="inspector-helper">{labels.conflictPairHint}</span>
          </label>
          {renderListArea(labels.charactersPresent, ['characters_present'], sharedCharacterHint, true)}
          {renderListArea(labels.events, ['events'], sharedEventHint, true)}
          {renderTextArea(labels.scriptText, ['script_text'], { rows: 6, full: true })}
        </div>
      )}

      {selected.type === 'event' && (
        <div className="inspector-grid">
          {renderTextInput(labels.eventType, ['event_type'])}
          {renderSelect(labels.act, ['act'], enumOptions.ActType)}
          <label className="inspector-block">
            <span className="form-label">{labels.scene}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['scene_id'], ''))}
              onChange={(event) => apply(['scene_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {scenes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {renderSelect(labels.beat, ['beat'], enumOptions.BeatType)}
          {renderSelect(labels.conflictType, ['conflict_type'], enumOptions.ConflictType)}
          {renderSelect(labels.emotionalImpact, ['emotional_impact'], enumOptions.EmotionType)}
          {renderTextArea(labels.description, ['description'], { full: true })}
          {renderListArea(labels.charactersInvolved, ['characters_involved'], sharedCharacterHint, true)}
          {renderListArea(labels.consequences, ['consequences'], text.linesHint, true)}
          {renderListArea(labels.foreshadowing, ['foreshadowing'], text.linesHint, true)}
        </div>
      )}

      {selected.type === 'plot_device' && (
        <div className="inspector-grid">
          <label className="inspector-block">
            <span className="form-label">{labels.deviceType}</span>
            <input
              className="app-input"
              list="plot-device-type-options"
              value={String(getValue(draftData, ['device_type'], ''))}
              onChange={(event) => apply(['device_type'], event.target.value)}
            />
            <datalist id="plot-device-type-options">
              {deviceTypes.map((item) => (
                <option key={item.type} value={item.type} />
              ))}
            </datalist>
            <span className="inspector-helper">
              {labels.deviceHint}
              {knownDevice?.short_drama_function ? ` ${knownDevice.short_drama_function}` : ''}
              {knownDevice?.expected_evidence?.length ? ` ${knownDevice.expected_evidence.join(' / ')}` : ''}
            </span>
            {!!knownDeviceTags.length && (
              <div className="inspector-tags">
                {knownDeviceTags.slice(0, 8).map((tag) => (
                  <span key={tag} className="inspector-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </label>
          <label className="inspector-block">
            <span className="form-label">{labels.attachedScene}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['attached_scene_id'], ''))}
              onChange={(event) => apply(['attached_scene_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {scenes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="inspector-block">
            <span className="form-label">{labels.attachedEvent}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['attached_event_id'], ''))}
              onChange={(event) => apply(['attached_event_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {events.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {renderTextArea(labels.description, ['description'], { full: true })}
          <label className="inspector-block inspector-block--full">
            <span className="form-label">{labels.setupNodes}</span>
            <textarea
              className="app-textarea app-textarea--compact"
              rows={4}
              value={formatBindingLines(getValue(draftData, ['setup_nodes'], []))}
              placeholder={labels.setupHint}
              onChange={(event) => apply(['setup_nodes'], parseBindingLines(event.target.value))}
            />
            <span className="inspector-helper">{labels.setupHint}</span>
          </label>
          <label className="inspector-block inspector-block--full">
            <span className="form-label">{labels.payoffNodes}</span>
            <textarea
              className="app-textarea app-textarea--compact"
              rows={4}
              value={formatBindingLines(getValue(draftData, ['payoff_nodes'], []))}
              placeholder={labels.setupHint}
              onChange={(event) => apply(['payoff_nodes'], parseBindingLines(event.target.value))}
            />
          </label>
          {renderListArea(labels.linkScenes, ['links', 'scenes'], sharedSceneHint, true)}
          {renderListArea(labels.linkCharacters, ['links', 'characters'], sharedCharacterHint, true)}
          {renderListArea(labels.linkRelationships, ['links', 'relationships'], text.linesHint, true)}
          {renderListArea(labels.linkBeats, ['links', 'beats'], text.linesHint, true)}
          {renderListArea(labels.linkWorldRules, ['links', 'world_rules'], text.linesHint, true)}
          {renderListArea(labels.linkProps, ['links', 'props'], text.linesHint, true)}
          {renderListArea(labels.dramaTags, ['short_drama_tags'], text.linesHint, true)}
          {renderListArea(labels.catharsisTypes, ['recommended_catharsis_types'], text.linesHint, true)}
          {renderListArea(labels.catharsisTagPool, ['catharsis_tag_pool'], text.linesHint, true)}
          {renderListArea(labels.reversalTypes, ['recommended_reversal_types'], text.linesHint, true)}
          {renderTextInput(labels.narrativeLayer, ['narrative_layer'])}
          {renderTextInput(labels.ownerAgent, ['owner_agent'])}
        </div>
      )}

      {selected.type === 'dialogue' && (
        <div className="inspector-grid">
          {renderTextInput(labels.speaker, ['speaker'])}
          <label className="inspector-block">
            <span className="form-label">{labels.scene}</span>
            <select
              className="app-input"
              value={String(getValue(draftData, ['scene_id'], ''))}
              onChange={(event) => apply(['scene_id'], event.target.value || undefined)}
            >
              <option value="">{labels.none}</option>
              {scenes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {renderSelect(labels.emotion, ['emotion'], enumOptions.EmotionType)}
          {renderTextArea(labels.content, ['content'], { rows: 5, full: true })}
          {renderTextArea(labels.subtext, ['subtext'])}
          {renderTextArea(labels.dialoguePurpose, ['purpose'])}
          {renderTextArea(labels.characterDevelopment, ['character_development'])}
          {renderTextArea(labels.plotAdvancement, ['plot_advancement'])}
        </div>
      )}
    </div>
  );
};

export default ElementInspectorFields;
